import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../lib/auth.jsx";
import { PRODUCTS } from "../lib/products.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_WA_NUMBER = "34637737339";

const CHANNEL_META = {
  web: { icon: "fa-solid fa-globe", color: "text-[#5c6b61]", bg: "bg-[#e5e8e2]", label: "Chat web" },
  whatsapp: { icon: "fa-brands fa-whatsapp", color: "text-[#25D366]", bg: "bg-[#25D366]/10", label: "WhatsApp" },
  instagram: { icon: "fa-brands fa-instagram", color: "text-[#E4405F]", bg: "bg-[#E4405F]/10", label: "Instagram" },
  facebook: { icon: "fa-brands fa-facebook", color: "text-[#1877F2]", bg: "bg-[#1877F2]/10", label: "Facebook" },
};

function relTime(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function ProductTemplateMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="text-xs eu-btn-outline py-1.5 px-3" data-testid="wa-template-trigger">
        <i className="fa-solid fa-bolt text-[10px]"></i> Plantilla
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-[#e5e8e2] rounded-xl shadow-lg z-10 w-64 overflow-hidden">
          {PRODUCTS.map(p => (
            <button key={p.id} onClick={() => { onPick(p); setOpen(false); }} className="block w-full text-left px-3 py-2 text-xs hover:bg-[#f7f7f4]" data-testid={`wa-template-${p.id}`}>
              <span className="font-mono-eu text-[9px] text-[#b45f3e]">{p.npk}</span>
              <div className="text-[#133322] font-medium">{p.name.es}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function buildProductTemplate(p) {
  return `Hola, te paso info técnica de *${p.name.es}* (${p.npk}):

✔ ${p.tagline.es}
✔ Dosis foliar: ${p.dosage.foliar.es}
✔ Dosis fertirriego: ${p.dosage.fertigation.es}
✔ Cultivos: ${p.crops.es.slice(0, 3).join(", ")}

¿Te ayudo a hacer una recomendación para tu cultivo? — Equipo EUZERONE`;
}

export default function AdminInbox() {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const fetchInbox = async () => {
    try {
      const res = await axios.get(`${API}/admin/inbox`, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(res.data.conversations || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (token) fetchInbox(); }, [token]);

  const openThread = async (conv) => {
    setSelected(conv);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/inbox/${encodeURIComponent(conv.session_id)}`, { headers: { Authorization: `Bearer ${token}` } });
      setThread(res.data.messages || []);
    } catch { setThread([]); } finally { setLoading(false); }
  };

  const channels = ["all", "web", "whatsapp", "instagram", "facebook"];
  const filtered = filter === "all" ? conversations : conversations.filter(c => c.channel === filter);

  const replyOnPlatform = (conv, customText = "") => {
    const text = customText || "";
    if (conv.channel === "whatsapp") {
      const phone = (conv.from || "").replace(/[^0-9]/g, "");
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
    } else if (conv.channel === "instagram") {
      window.open(`https://ig.me/m/${conv.from}`, "_blank");
    } else if (conv.channel === "facebook") {
      window.open(`https://m.me/${conv.from}`, "_blank");
    } else {
      navigator.clipboard?.writeText(text);
      alert("Texto copiado al portapapeles. Pega la respuesta en el canal correspondiente.");
    }
  };

  return (
    <div className="eu-card overflow-hidden" data-testid="admin-inbox-tab">
      <div className="grid lg:grid-cols-12 min-h-[560px]">
        {/* Sidebar */}
        <aside className="lg:col-span-4 border-r border-[#e5e8e2] flex flex-col">
          <div className="p-4 border-b border-[#e5e8e2]">
            <div className="eu-data-label mb-3">BANDEJA UNIFICADA</div>
            <div className="flex flex-wrap gap-1">
              {channels.map(c => (
                <button key={c} onClick={() => setFilter(c)} className={`text-[10px] uppercase font-mono-eu tracking-wider rounded-full px-3 py-1 ${filter===c?"bg-[#133322] text-[#f7f7f4]":"bg-[#f7f7f4] text-[#5c6b61] hover:text-[#133322]"}`} data-testid={`inbox-filter-${c}`}>
                  {c === "all" ? "Todos" : CHANNEL_META[c]?.label || c}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[500px]">
            {filtered.length === 0 && (<div className="p-10 text-center text-sm text-[#5c6b61]">Sin conversaciones</div>)}
            {filtered.map(conv => {
              const meta = CHANNEL_META[conv.channel] || CHANNEL_META.web;
              const isActive = selected?.session_id === conv.session_id;
              return (
                <button key={conv.session_id} onClick={() => openThread(conv)} className={`w-full text-left p-4 border-b border-[#e5e8e2] hover:bg-[#f7f7f4] transition ${isActive?"bg-[#f7f7f4]":""}`} data-testid={`inbox-conv-${conv.session_id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${meta.bg}`}>
                      <i className={`${meta.icon} ${meta.color}`}></i>
                    </span>
                    <span className="text-xs font-mono-eu text-[#5c6b61] truncate flex-1">{conv.from || conv.session_id}</span>
                    <span className="text-[10px] text-[#5c6b61]">{relTime(conv.last_at)}</span>
                  </div>
                  <div className="text-xs text-[#1c1c1c] line-clamp-2">{conv.last_msg || "—"}</div>
                  <div className="text-[10px] text-[#5c6b61] mt-1">{conv.count} msg</div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Thread view */}
        <section className="lg:col-span-8 flex flex-col bg-[#f7f7f4]/40">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
              <i className="fa-solid fa-comments text-5xl text-[#9ead9b]/40 mb-4"></i>
              <div className="font-display text-2xl text-[#133322]">Selecciona una conversación</div>
              <p className="text-sm text-[#5c6b61] mt-2 max-w-md">Aquí verás el historial unificado de chat web, WhatsApp, Instagram y Facebook Messenger.</p>
            </div>
          ) : (
            <>
              <header className="p-4 border-b border-[#e5e8e2] bg-white flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-9 w-9 rounded-full flex items-center justify-center ${CHANNEL_META[selected.channel]?.bg}`}>
                    <i className={`${CHANNEL_META[selected.channel]?.icon} ${CHANNEL_META[selected.channel]?.color}`}></i>
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium text-[#133322] truncate">{selected.from || selected.session_id}</div>
                    <div className="text-xs text-[#5c6b61]">{CHANNEL_META[selected.channel]?.label} · {selected.count} mensajes</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ProductTemplateMenu onPick={(p) => replyOnPlatform(selected, buildProductTemplate(p))} />
                  <button onClick={() => replyOnPlatform(selected, "")} className="text-xs eu-btn-primary py-1.5 px-3" data-testid="inbox-reply-btn">
                    <i className="fa-solid fa-reply text-[10px]"></i> Responder
                  </button>
                </div>
              </header>
              <div className="flex-1 overflow-y-auto p-6 space-y-3 max-h-[500px]">
                {loading && <div className="text-center text-sm text-[#5c6b61]">Cargando…</div>}
                {!loading && thread.length === 0 && <div className="text-center text-sm text-[#5c6b61]">Sin mensajes</div>}
                {thread.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-white border border-[#e5e8e2] rounded-bl-sm" : "bg-[#133322] text-[#f7f7f4] rounded-br-sm"}`}>
                      <div className="text-[10px] font-mono-eu opacity-60 mb-1">{m.role === "user" ? "USUARIO" : "FLORA"}</div>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
